import { useState, useEffect } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { SearchableSelect } from "../ui/SearchableSelect";

interface EnrollmentFiltersDialogProps {
  isOpen: boolean;
  onClose: () => void;
  filters: {
    status: string;
    type: string;
    courseId: string;
    minWatchTime: string; // in hours for UI, convert to seconds for API
    maxWatchTime: string;
    sortBandwidth: string;
    paymentMethod: string;
  };
  onApply: (filters: any) => void;
  courses: { _id: string; title: string }[];
}

export function EnrollmentFiltersDialog({
  isOpen,
  onClose,
  filters: initialFilters,
  onApply,
  courses,
}: EnrollmentFiltersDialogProps) {
  const [localFilters, setLocalFilters] = useState(initialFilters);

  // Sync local state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setLocalFilters(initialFilters);
    }
  }, [isOpen, initialFilters]);

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleReset = () => {
    const resetFilters = {
      status: "all",
      type: "all",
      courseId: "",
      minWatchTime: "",
      maxWatchTime: "",
      sortBandwidth: "none",
      paymentMethod: "all",
    };
    setLocalFilters(resetFilters);
    onApply(resetFilters);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Filter Enrollments">
      <div className="space-y-6 py-2">
        {/* Status Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Status</label>
          <div className="grid grid-cols-3 gap-2">
            {["all", "active", "completed", "expired", "cancelled"].map((s) => (
              <button
                key={s}
                onClick={() => setLocalFilters({ ...localFilters, status: s })}
                className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                  localFilters.status === s
                    ? "bg-[#102040] text-white border-[#102040]"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                }`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Enrollment Type */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Enrollment Type
          </label>
          <div className="flex gap-2">
            {[
              { id: "all", label: "All" },
              { id: "paid", label: "Paid" },
              { id: "free", label: "Free" },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setLocalFilters({ ...localFilters, type: t.id })}
                className={`flex-1 px-3 py-2 text-xs rounded-lg border transition-all ${
                  localFilters.type === t.id
                    ? "bg-[#102040] text-white border-[#102040]"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Payment Method Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Payment Method
          </label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: "all", label: "All" },            ].map((m) => (
              <button
                key={m.id}
                onClick={() =>
                  setLocalFilters({ ...localFilters, paymentMethod: m.id })
                }
                className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                  localFilters.paymentMethod === m.id
                    ? "bg-[#102040] text-white border-[#102040]"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {/* Course Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Filter by Course
          </label>
          <SearchableSelect
            options={courses.map((c) => ({ value: c._id, label: c.title }))}
            value={localFilters.courseId}
            onChange={(val) =>
              setLocalFilters({ ...localFilters, courseId: val })
            }
            placeholder="Select a course..."
          />
        </div>

        {/* Watch Time / Bandwidth */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Bandwidth Consumption (Hours)
          </label>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Input
                type="number"
                placeholder="Min Hours"
                value={localFilters.minWatchTime}
                onChange={(e) =>
                  setLocalFilters({
                    ...localFilters,
                    minWatchTime: e.target.value,
                  })
                }
                className="h-9 text-sm"
              />
            </div>
            <span className="text-gray-400">to</span>
            <div className="flex-1">
              <Input
                type="number"
                placeholder="Max Hours"
                value={localFilters.maxWatchTime}
                onChange={(e) =>
                  setLocalFilters({
                    ...localFilters,
                    maxWatchTime: e.target.value,
                  })
                }
                className="h-9 text-sm"
              />
            </div>
          </div>
        </div>

        {/* Bandwidth Sorting */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Sort by Bandwidth
          </label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: "none", label: "Default" },
              { id: "desc", label: "High to Low" },
              { id: "asc", label: "Low to High" },
            ].map((s) => (
              <button
                key={s.id}
                onClick={() =>
                  setLocalFilters({ ...localFilters, sortBandwidth: s.id })
                }
                className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                  localFilters.sortBandwidth === s.id
                    ? "bg-[#102040] text-white border-[#102040]"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between gap-3 pt-6 border-t border-gray-100">
          <Button
            variant="outline"
            onClick={handleReset}
            className="px-6 text-red-600 hover:bg-red-50 hover:border-red-100"
          >
            Reset All
          </Button>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="px-6">
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleApply}
              className="px-8 bg-[#e07828] hover:bg-[#c99800]"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
